import {
  FOLLOW_ARTIST_URL_PARAM_NAME,
  RETURN_FOLLOW_ARTIST_TOKEN_NAME,
  verifyReturnToken,
} from '@app/components/SignUpGuard/utils';
import { useSignUpGuardContext } from '@app/hooks/SignUpGuardContext';
import {
  ARTIST_FOLLOW_PUBLIC_ENDPOINT,
  ARTIST_UNFOLLOW_PUBLIC_ENDPOINT,
} from '@app/pages/api/constants';
import { UserInfo } from '@redbubble/next/types';
import router from 'next/router';
import { useCallback, useEffect, useState } from 'react';

const postArtistAction = async (path: string, artistId: number) => {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ artistId }),
  });

  if (!res.ok) return null;
  return res.json();
};

const followArtist = (id: number) =>
  postArtistAction(ARTIST_FOLLOW_PUBLIC_ENDPOINT, id);
const unfollowArtist = (id: number) =>
  postArtistAction(ARTIST_UNFOLLOW_PUBLIC_ENDPOINT, id);

export const useFollowArtist = ({
  isLoggedIn,
  artistId,
  initialFollowingStatus,
}: {
  isLoggedIn: UserInfo['isLoggedIn'];
  artistId: number;
  initialFollowingStatus: boolean;
}) => {
  const { signUpGuard, setSignUpGuard } = useSignUpGuardContext();
  const [followingArtist, setFollowingArtist] = useState(
    initialFollowingStatus
  );

  const handleOnToggleFollowArtist = useCallback(() => {
    if (!isLoggedIn) {
      setSignUpGuard({
        ...signUpGuard,
        open: true,
        actionToken: RETURN_FOLLOW_ARTIST_TOKEN_NAME,
      });
      return;
    }

    if (Number.isNaN(artistId)) {
      throw new Error('Cannot follow artist. Missing artist ID');
    }

    if (followingArtist) {
      unfollowArtist(artistId).then((res) => res && setFollowingArtist(false));
      return;
    }
    followArtist(artistId).then((res) => res && setFollowingArtist(true));
  }, [isLoggedIn, artistId, followingArtist, signUpGuard, setSignUpGuard]);

  useEffect(() => {
    // This will remove "follow_artist" from the url but keep everything else intact. Allows re-use
    // across any page by replacing everything that was in the query beforehand but maintain pathname structure.
    const {
      [FOLLOW_ARTIST_URL_PARAM_NAME]: followArtistToken,
      ...remainingQuery
    } = router.query;

    if (typeof followArtistToken !== 'string' || !followArtistToken) return;

    const followArtistTokenMatches = verifyReturnToken(
      followArtistToken,
      RETURN_FOLLOW_ARTIST_TOKEN_NAME
    );

    if (followArtistTokenMatches && !followingArtist) {
      handleOnToggleFollowArtist();
    }

    router.replace(
      { pathname: router.pathname, query: remainingQuery },
      undefined,
      { shallow: true }
    );
  }, [handleOnToggleFollowArtist, followingArtist]);

  return {
    followingArtist,
    handleOnToggleFollowArtist,
  };
};
